import { useEffect, useState } from "react";
import { api, formatDateFull, todayISO, formatApiError } from "@/api";
import { Plus, X, CheckCircle2, Clock, Pause, Save, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "tamamlandi", label: "Tamamlandı", icon: CheckCircle2, color: "#10b981" },
  { value: "devam", label: "Devam ediyor", icon: Clock, color: "#f59e0b" },
  { value: "beklemede", label: "Beklemede", icon: Pause, color: "#64748b" },
];

export default function MyReportPage() {
  const [freeText, setFreeText] = useState("");
  const [tasks, setTasks] = useState([]);
  const [hoursWorked, setHoursWorked] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/mine/today");
      if (data.exists) {
        setExisting(true);
        setFreeText(data.free_text || "");
        setTasks(data.tasks || []);
        setHoursWorked(data.hours_worked || "");
        setLastSaved(data.updated_at || data.created_at);
      } else {
        setExisting(false);
      }
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addTask = () => setTasks([...tasks, { title: "", status: "beklemede", note: "" }]);
  const updateTask = (i, patch) => setTasks(tasks.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const removeTask = (i) => setTasks(tasks.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!freeText.trim() && tasks.length === 0) {
      setError("En az bir metin ya da görev yazın.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        free_text: freeText,
        tasks: tasks.filter(t => (t.title || "").trim()),
        hours_worked: Number(hoursWorked) || 0,
      };
      await api.post("/reports", payload);
      toast.success(existing ? "Rapor güncellendi" : "Rapor kaydedildi");
      await load();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "Kaydedilemedi"));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-block">Yükleniyor…</div>;

  return (
    <>
      <div className="page-heading compact-heading">
        <div>
          <p className="eyebrow">Günlük Rapor</p>
          <h1>{formatDateFull(todayISO())}</h1>
          <p className="subheading">
            {existing
              ? "Bugün için zaten bir raporunuz var — düzenleyip kaydedebilirsiniz."
              : "Bugün ne üzerinde çalıştığınızı kısaca paylaşın. Görev ekleyerek ilerleme kaydedin."}
          </p>
        </div>
      </div>

      {existing && lastSaved && (
        <div className="status-strip" data-testid="my-report-status">
          <span className="status-dot"/> Rapor var · son kaydedilen: {new Date(lastSaved).toLocaleString("tr-TR")}
        </div>
      )}

      <form className="report-form" onSubmit={submit} style={{ marginTop: 20 }}>
        <div className="panel" style={{ padding: 24 }}>
          <label className="rf-label">Bugün ne yaptın? <span className="cell-muted">(uzun yazabilirsiniz)</span></label>
          <textarea
            className="report-textarea"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Bugün üzerinde çalıştığınız konuları, karşılaştığınız durumları ve yarın yapacaklarınızı yazın…"
            rows={6}
            data-testid="free-text-input"
          />

          <div className="form-grid" style={{ marginTop: 16 }}>
            <label>
              Çalışma süresi (saat)
              <input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={hoursWorked}
                onChange={(e) => setHoursWorked(e.target.value)}
                placeholder="Örn: 8"
                data-testid="hours-input"
              />
            </label>
          </div>
        </div>

        <div className="panel" style={{ padding: 24, marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <FileText size={16} style={{ color: "#10b981" }}/>
            <strong>Görevler</strong>
            <span className="cell-muted" style={{ marginLeft: 4 }}>({tasks.length})</span>
            <button type="button" className="ghost-button" style={{ marginLeft: "auto" }} onClick={addTask} data-testid="add-task-btn">
              <Plus size={14}/> Görev ekle
            </button>
          </div>

          {tasks.length === 0 ? (
            <div className="empty-state small">
              <p>Henüz görev eklenmedi. "Görev ekle" ile başlayın.</p>
            </div>
          ) : (
            <div className="task-form-list">
              {tasks.map((t, i) => (
                <div key={i} className="task-form-item" data-testid={`task-item-${i}`}>
                  <div className="task-form-row">
                    <input
                      type="text"
                      placeholder="Görev başlığı"
                      value={t.title}
                      onChange={(e) => updateTask(i, { title: e.target.value })}
                      className="task-title-input"
                      data-testid={`task-title-${i}`}
                    />
                    <select
                      value={t.status}
                      onChange={(e) => updateTask(i, { status: e.target.value })}
                      data-testid={`task-status-${i}`}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button type="button" className="icon-btn danger" onClick={() => removeTask(i)} data-testid={`task-remove-${i}`}>
                      <X size={14}/>
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Not (opsiyonel)"
                    value={t.note}
                    onChange={(e) => updateTask(i, { note: e.target.value })}
                    className="task-note-input"
                    data-testid={`task-note-${i}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="form-error" data-testid="my-report-error" style={{ marginTop: 14 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="submit" className="primary-button" disabled={saving} data-testid="save-report-btn">
            <Save size={16}/> {saving ? "Kaydediliyor…" : (existing ? "Güncelle" : "Kaydet")}
          </button>
        </div>
      </form>
    </>
  );
}
